import {
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  FormControl,
  FormLabel,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { useFetchAccountsQuery } from 'api';
import { PostPurchaseOrderRequest } from 'api/bindings/PostPurchaseOrderRequest';
import { PostPurchaseOrderTransactionRequest } from 'api/bindings/PostPurchaseOrderTransactionRequest';
import { PutPurchaseOrderRequest } from 'api/bindings/PutPurchaseOrderRequest';
import { SubAccountResponse } from 'api/bindings/SubAccountResponse';
import { useAuth } from 'core';
import Decimal from 'decimal.js';
import {
  AppInput,
  CurrencyLabel,
  FormButtonGroup,
  SubAccountLabel,
} from 'features/components';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

type PurchaseOrderModel = PostPurchaseOrderRequest | PutPurchaseOrderRequest;

function isPostRequest(
  dto: PurchaseOrderModel
): dto is PostPurchaseOrderRequest {
  return !('id' in dto);
}
function isPutRequest(dto: PurchaseOrderModel): dto is PutPurchaseOrderRequest {
  return 'id' in dto;
}
export interface PurchaseOrderFormRevisionDrawerContentProps
  extends Omit<DrawerContentProps, 'onSubmit'> {
  po: PurchaseOrderModel;
  onSubmit: (form: Form) => void;
  onClose: () => void;
}

type Form = PostPurchaseOrderTransactionRequest;
type AccountModel = { subAccount: SubAccountResponse; total: string };
type AccountMap = { [key: number]: AccountModel };

export const PurchaseOrderFormRevisionDrawerContent = (
  props: PurchaseOrderFormRevisionDrawerContentProps
) => {
  const { po, onSubmit, onClose, ...rest } = props;
  const auth = useAuth();
  const { data: accountsResponse } = useFetchAccountsQuery(
    { clientId: auth?.user.clientId ?? '0' },
    { skip: !auth?.user.clientId }
  );

  let allSubAccounts = useMemo(
    () => accountsResponse?.items.flatMap((x) => x.subAccounts),
    [accountsResponse?.items]
  );

  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {
      userId: undefined,
    },
  });
  const { register, watch, handleSubmit } = form;

  const liTotals = useMemo(() => {
    if (isPutRequest(po)) {
      return po?.lineItems?.reduce<AccountMap>((sum, n) => {
        const subAccount = allSubAccounts?.find(
          (x) => x.subAccountId === n.subAccountId
        );
        if (!subAccount) return sum;
        const dExisting = new Decimal(sum[Number(n.subAccountId)]?.total || 0);
        sum[Number(n.subAccountId)] = {
          subAccount,
          total: dExisting
            .add(n.unitPrice || '0')
            .times(n.quantityOrdered || '0')
            .toString(),
        };
        return sum;
      }, {});
    } else {
      return {};
    }
  }, [allSubAccounts, po]);

  const formName = watch('name');
  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DrawerContent {...rest}>
          <DrawerCloseButton />
          <DrawerHeader>PO Revision: {formName}</DrawerHeader>
          <DrawerBody>
            <Stack spacing={4}>
              <FormControl>
                <FormLabel htmlFor="name">Name:</FormLabel>
                <AppInput
                  id="name"
                  {...register('name', {
                    required: 'Name is required',
                    minLength: {
                      value: 3,
                      message: 'Name must be between 3 and 256 characters',
                    },
                    maxLength: {
                      value: 256,
                      message: 'Name must be between 3 and 256 characters',
                    },
                  })}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Accounts:</FormLabel>
                <TableContainer>
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Account</Th>
                        <Th>Change</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {liTotals &&
                        Object.keys(liTotals)
                          .map((x) => liTotals[x])
                          .map((li: AccountModel, i) => (
                            <Tr key={i}>
                              <Td>
                                <SubAccountLabel value={li.subAccount} />
                              </Td>
                              <Td>
                                <CurrencyLabel value={li.total} />
                              </Td>
                            </Tr>
                          ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </FormControl>
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <FormButtonGroup
              onCancel={onClose}
              onConfirm={handleSubmit(onSubmit)}
            />
          </DrawerFooter>
        </DrawerContent>
      </form>
    </FormProvider>
  );
};
